import { decryptData, encryptData } from '@/utils/encryption';

/**
 * Creates a Zustand persist storage adapter with TTL (Time To Live) support
 * 
 * @param {string} storeName - Name of the store (for logging)
 * @param {number} ttl - Time to live in milliseconds (default: 24 hours)
 * @returns {object} Storage adapter for Zustand persist middleware
 * 
 * @example
 * const useMyStore = create(
 *   persist(
 *     (set) => ({ ... }),
 *     {
 *       name: 'my-store',
 *       storage: createLocalStorageWithTTL('my-store', 24 * 60 * 60 * 1000),
 *     }
 *   )
 * );
 */
export function createLocalStorageWithTTL(storeName, ttl = 12 * 60 * 60 * 1000) {
  return {
    getItem: (name) => {
      try {
        // Check if we're in browser environment
        if (typeof window === 'undefined' || !window.localStorage) {
          return null;
        }

        const storedValue = localStorage.getItem(name);
        if (!storedValue) return null;

        const decrypted = decryptData(storedValue);
        if (!decrypted) {
          console.warn(`[${storeName}] Failed to decrypt localStorage data. Clearing...`);
          localStorage.removeItem(name);
          return null;
        }

        // Check if data has timestamp wrapper
        if (decrypted.timestamp && decrypted.data) {
          const now = Date.now();
          const age = now - decrypted.timestamp;

          // If data is older than TTL, clear it and return null
          if (age > ttl) {
            const hoursOld = Math.round(age / 1000 / 60 / 60);
            console.warn(
              `[${storeName}] Data expired (${hoursOld}h old, TTL: ${Math.round(ttl / 1000 / 60 / 60)}h). Clearing localStorage.`
            );
            localStorage.removeItem(name);
            return null;
          }

          // Data is still valid, return it
          return decrypted.data;
        }

        // Legacy data without timestamp (from old code)
        // Accept it but it will get timestamp on next save
        console.info(`[${storeName}] Migrating legacy localStorage data to TTL format`);
        return decrypted;
      } catch (error) {
        console.error(`[${storeName}] Error reading from localStorage:`, error);
        // Clear corrupted data
        try {
          localStorage.removeItem(name);
        } catch (e) {
          console.error(`[${storeName}] Failed to clear corrupted data:`, e);
        }
        return null;
      }
    },

    setItem: (name, value) => {
      try {
        // Check if we're in browser environment
        if (typeof window === 'undefined' || !window.localStorage) {
          return;
        }

        // Wrap data with timestamp
        const dataWithTimestamp = {
          timestamp: Date.now(),
          data: value,
        };

        localStorage.setItem(name, encryptData(dataWithTimestamp));
      } catch (error) {
        // Handle quota exceeded or other errors
        if (error.name === 'QuotaExceededError') {
          console.error(`[${storeName}] localStorage quota exceeded. Clearing old data...`);
          try {
            localStorage.removeItem(name);
            localStorage.setItem(name, encryptData({ timestamp: Date.now(), data: value }));
          } catch (e) {
            console.error(`[${storeName}] Failed to save even after clearing:`, e);
          }
        } else {
          console.error(`[${storeName}] Error saving to localStorage:`, error);
        }
      }
    },

    removeItem: (name) => {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem(name);
        }
      } catch (error) {
        console.error(`[${storeName}] Error removing from localStorage:`, error);
      }
    },
  };
}

/**
 * Predefined TTL constants
 */
export const TTL = {
  ONE_HOUR: 60 * 60 * 1000,
  SIX_HOURS: 6 * 60 * 60 * 1000,
  TWELVE_HOURS: 12 * 60 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000,
  THREE_DAYS: 3 * 24 * 60 * 60 * 1000,
  ONE_WEEK: 7 * 24 * 60 * 60 * 1000,
};

